import { useMemo } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

const BILLING_METHOD_OPTIONS = ["Wallet", "Card"];
const PRIVILEGE_OPTIONS = [
  { value: "API_ACCESS", label: "API Access" },
  { value: "FLIGHT_BOOKING", label: "Flight Booking" },
  { value: "AGENT_LINK", label: "Agent Link" },
  { value: "CUSTOMER_INVOICING", label: "Customer Invoicing" },
  { value: "TEAM_ACCESS", label: "Team Access" },
  {
    value: "CUSTOM_SUPPLIER_CREDENTIALS",
    label: "Custom Supplier Credentials",
  },
  {
    value: "CUSTOM_PAYSTACK_CREDENTIALS",
    label: "Custom Paystack Credentials",
  },
  { value: "SMS_NOTIFICATION", label: "SMS Notification" },
  { value: "PARTIAL_WIDGET_STYLING", label: "Partial Widget Styling" },
  { value: "FULL_WIDGET_STYLING", label: "Full Widget Styling" },
];

const normalizeList = (values = []) =>
  values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index);

export default function EditAddonDialog({
  open,
  editMode,
  formData,
  setFormData,
  onClose,
  onSubmit,
  isPending,
}) {
  const availablePrivilegeOptions = useMemo(() => {
    const existingOptions = (formData?.privileges || [])
      .filter(
        (value) =>
          !PRIVILEGE_OPTIONS.some((option) => option.value === value),
      )
      .map((value) => ({
        value,
        label: value,
      }));

    return [...PRIVILEGE_OPTIONS, ...existingOptions];
  }, [formData?.privileges]);

  const selectedPrivileges = useMemo(
    () =>
      availablePrivilegeOptions.filter((option) =>
        (formData?.privileges || []).includes(option.value),
      ),
    [availablePrivilegeOptions, formData?.privileges],
  );

  const isSubmitDisabled =
    isPending ||
    !String(formData?.name || "").trim() ||
    !String(formData?.description || "").trim();

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "18px",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" className="font-bold text-slate-900">
          {editMode ? "Edit Add-on" : "Create Add-on"}
        </Typography>
        <Typography variant="body2" className="text-slate-500 mt-1">
          Configure pricing, privileges, billing methods, and active status.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Add-on Name"
              name="name"
              value={formData.name}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  name: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.isActive)}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: event.target.checked,
                    }))
                  }
                />
              }
              label={formData.isActive ? "Active" : "Inactive"}
              sx={{ mt: { xs: 0, md: 1 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              minRows={3}
              label="Description"
              name="description"
              value={formData.description}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Monthly Price"
              inputProps={{ min: 0 }}
              value={formData.priceMonthly}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  priceMonthly: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Annual Price"
              inputProps={{ min: 0 }}
              value={formData.priceAnnual}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  priceAnnual: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={availablePrivilegeOptions}
              value={selectedPrivileges}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={(option) => option.label}
              onChange={(_event, value) =>
                setFormData((prev) => ({
                  ...prev,
                  privileges: normalizeList(
                    value.map((option) => option.value),
                  ),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${option.value}-${index}`}
                    label={option.label}
                    size="small"
                    sx={{
                      bgcolor: "#DBEAFE",
                      color: "#1D4ED8",
                      fontWeight: 600,
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Privileges"
                  placeholder="Select privileges"
                  helperText="Select one or more privileges for this add-on."
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Box className="flex flex-wrap gap-1">
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{
                          bgcolor: "#EDE9FE",
                          color: "#6D28D9",
                          fontWeight: 600,
                        }}
                      />
                    ))}
                  </Box>
                ),
              }}
              label="Allowed Billing Methods"
              value={formData.allowedBillingMethods}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  allowedBillingMethods: event.target.value,
                }))
              }
              helperText="Choose how customers can pay for this add-on."
            >
              {BILLING_METHOD_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={isPending}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#475569",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitDisabled}
          variant="contained"
          className="bg-primary1"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 2.5,
            "&:hover": {
              bgcolor: "inherit",
            },
          }}
        >
          {isPending ? "Saving..." : editMode ? "Update Add-on" : "Create Add-on"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
